import { type ApolloClient, gql } from '@apollo/client/core';
import type { Mutation, Query, SaveDefaultSetMutationInput } from '../../../generated/api.graphql';
import type { VPError } from '../../../VPError';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as DefaultSet from './DefaultSet';
import * as DefaultSetInput from './DefaultSetInput';
import { DEFAULT_SET_FIELDS } from './fragments';

export const DEFAULT_SET_QUERY = gql`
  query DefaultSet($venueId: Int) {
    defaultSet(venueId: $venueId) {
      ...DefaultSetFields
    }
  }
  ${DEFAULT_SET_FIELDS}
`;

export const SAVE_DEFAULT_SET_MUTATION = gql`
  mutation SaveDefaultSet($input: SaveDefaultSetMutationInput!) {
    saveDefaultSet(input: $input) {
      success
      defaultSet {
        ...DefaultSetFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${DEFAULT_SET_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const getDefaultSet = async (
  client: ApolloClient<Query>,
  venueId?: number,
): Promise<DefaultSet.DefaultSet | undefined> => {
  const { data } = await client.query<Query>({
    query: DEFAULT_SET_QUERY,
    variables: { venueId },
    fetchPolicy: 'network-only',
  });

  return data.defaultSet ? DefaultSet.fromGql(data.defaultSet) : undefined;
};

export const saveDefaultSet = async (
  client: ApolloClient<Mutation>,
  { venueId, defaultSet }: { venueId?: number; defaultSet: DefaultSetInput.DefaultSetInput },
): Promise<VPResult<DefaultSet.DefaultSet, VPError[]>> => {
  const input: SaveDefaultSetMutationInput = {
    venueId,
    defaultSet: DefaultSetInput.toGqlInput(defaultSet),
  };

  const { data } = await client.mutate<Mutation>({ mutation: SAVE_DEFAULT_SET_MUTATION, variables: { input } });

  const payload = data?.saveDefaultSet;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);
  if (errors.length) {
    return { success: false, errors };
  }

  if (!payload?.success || !payload.defaultSet) {
    return { success: false, errors: [{ path: '', message: 'Failed to save the default set.' }] };
  }

  return { success: true, data: DefaultSet.fromGql(payload.defaultSet) };
};
